import type { RoleSummary } from '@/features/authorization'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** Same bar as every other invite-shaped form in this codebase (see
 * `CreateTenantModal`): non-empty, one `@`, a dot after it. Good enough to
 * gate the submit button -- the server's own `EmailStr` is the real check. */
export function isValidInviteEmail(email: string): boolean {
  return EMAIL_RE.test(email.trim())
}

/**
 * The only role keys `POST /settings/members/invite` accepts
 * (`InviteRequest.role` on the server is a closed `Literal` of exactly these
 * four). Custom roles are deliberately not invite-able yet -- offering one
 * here would submit a role the server is guaranteed to reject.
 */
const INVITEABLE_ROLE_KEYS: ReadonlySet<string> = new Set([
  'owner', 'admin', 'member', 'viewer',
])

/**
 * Roles the current viewer may invite someone into.
 *
 * A protected role (Owner) is grantable only by somebody who already holds
 * it -- `role.canEdit` is the server's answer to that, the same gate
 * `MembersSettings` uses to disable a role-change item for a role it cannot
 * assign. Left out of the invite picker entirely rather than shown-and-
 * disabled: there is no row to anchor a disabled option's explanation to in a
 * plain `<select>`.
 *
 * Also excludes any custom (non-system) role -- see `INVITEABLE_ROLE_KEYS`.
 */
export function visibleInviteRoles(roles: RoleSummary[]): RoleSummary[] {
  return roles.filter(
    (role) => INVITEABLE_ROLE_KEYS.has(role.key) && (!role.isProtected || role.canEdit),
  )
}
